import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { datadogRum } from '@datadog/browser-rum';
import { useDispatch } from 'react-redux';
import { AggregatorCredentialsForm } from './AggregatorCredentialsForm';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import type { AccountConnectionError } from '@/hooks/useAccountConnectionError';
import { useLocalization } from '@/hooks/useLocalization';
import { accountsApi } from '@/store/accountsApi';
import {
  useGetAggregatorCredentialsQuery,
  useUpdateAggregatorCredentialsMutation,
} from '@/store/aggregatorCredentialsApi';
import type { AppDispatch } from '@/store/store';
import { wealthsApi } from '@/store/wealthsApi';
import {
  AggregatorCredentialsFormType,
  AggregatorFormStatus,
  isFailureTerminalFormType,
  isSupportedFormType,
  type AggregatorCredentialsModel,
} from '@/utils/aggregatorCredentials';

interface ReconnectAggregatorIntegrationProps {
  institutionName: string;
  connectionError: AccountConnectionError;
  onComplete: () => void;
  onCancel: () => void;
  onError: () => void;
}

/**
 * Reconnect flow for aggregators that describe their credentials declaratively
 * (ByAll, Demo). The backend returns a form, the user fills it, we PUT it back; if
 * the answer is another form we render that instead — which is how MFA works
 * without any wizard code. Ported from legacy `SetupCredentials.jsx:190-236`.
 *
 * Note there is no `onProcessing`, unlike the Yodlee flow. Switching the modal to
 * its 'processing' view would unmount this component mid-submit and discard the
 * next form, stranding the user. Submit feedback stays in-place instead.
 *
 * Yodlee is not served here: FastLink is an opaque iframe and has its own component.
 */
export const ReconnectAggregatorIntegration: React.FC<
  ReconnectAggregatorIntegrationProps
> = ({ institutionName, connectionError, onComplete, onCancel, onError }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useLocalization();
  const { showSnackbar } = useGlobalSnackbar();
  const [updateAggregatorCredentials] =
    useUpdateAggregatorCredentialsMutation();

  const { data, isLoading, isError } = useGetAggregatorCredentialsQuery({
    aggregatorId: connectionError.aggregatorId,
    wealthId: connectionError.wealthId,
  });

  const [model, setModel] = useState<AggregatorCredentialsModel | undefined>();
  /** Bumped per step so the form remounts and drops the previous answers. */
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A failed initial GET must not leave the user watching a spinner forever.
  useEffect(() => {
    if (isError) {
      onError();
    }
  }, [isError, onError]);

  /**
   * Success is reported once, from wherever it is reached. Without this the
   * terminal-success effect below would re-run — and re-invalidate, and
   * re-announce — on any render where its dependencies change identity.
   */
  const hasFinishedRef = useRef(false);

  const finishSuccessfully = useCallback((): void => {
    if (hasFinishedRef.current) {
      return;
    }
    hasFinishedRef.current = true;

    // The aggregator confirmed the credentials, so the backend already reset the
    // stored error. Refetching is what surfaces it — error flag, balances and
    // last-updated together. Both slices need their own invalidation: they are
    // separate createApi instances with no shared tag registry.
    dispatch(wealthsApi.util.invalidateTags([{ type: 'Wealths', id: 'LIST' }]));
    dispatch(
      accountsApi.util.invalidateTags([{ type: 'Accounts', id: 'LIST' }]),
    );
    showSnackbar(
      t('connectionError.reconnectSuccess', { name: institutionName }),
    );
    onComplete();
  }, [dispatch, institutionName, onComplete, showSnackbar, t]);

  const initialType = data?.CredentialsForm.Type;

  // The very first form can already be terminal: an unrecoverable error, or an
  // account the aggregator considers connected. Neither is a form to fill in.
  useEffect(() => {
    if (initialType === undefined) {
      return;
    }
    if (isFailureTerminalFormType(initialType)) {
      onError();
      return;
    }
    if (initialType === AggregatorCredentialsFormType.AuthenticationSucceeded) {
      // Same cleanup as a submitted form: finishing without invalidating would
      // leave the modal claiming success over whatever the last fetch returned.
      finishSuccessfully();
    }
  }, [initialType, finishSuccessfully, onError]);

  useEffect(() => {
    if (data) {
      setModel(data);
    }
  }, [data]);

  const current = model ?? data;

  const handleSubmit = async (
    filled: AggregatorCredentialsModel,
  ): Promise<void> => {
    setIsSubmitting(true);

    try {
      const result = await updateAggregatorCredentials({
        aggregatorId: connectionError.aggregatorId,
        model: filled,
      });

      if (result.error) {
        throw new Error('Aggregator credentials update failed.');
      }

      const next = result.data;
      const nextForm = next.CredentialsForm;

      if (nextForm.Status.StatusCode === AggregatorFormStatus.Success) {
        finishSuccessfully();
        return;
      }

      if (
        isFailureTerminalFormType(nextForm.Type) ||
        !isSupportedFormType(nextForm.Type)
      ) {
        // Only scalar diagnostics — a credentials payload must never reach RUM.
        datadogRum.addError(
          new Error('Aggregator reconnect ended without a usable next step'),
          {
            wealthId: connectionError.wealthId,
            aggregatorId: connectionError.aggregatorId,
            formType: nextForm.Type,
            statusCode: nextForm.Status.StatusCode,
          },
        );
        onError();
        return;
      }

      // Anything else is another step: render the new form and let the user
      // continue. Bumping `step` remounts the form so stale answers are dropped.
      setModel(next);
      setStep((value) => value + 1);
    } catch (error) {
      console.error('[ReconnectAggregatorIntegration] failed', error);
      datadogRum.addError(error, {
        wealthId: connectionError.wealthId,
        aggregatorId: connectionError.aggregatorId,
      });
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isError) {
    // The effect above already told the modal; render nothing meanwhile.
    return null;
  }

  if (isLoading || !current) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentType = current.CredentialsForm.Type;

  if (
    isFailureTerminalFormType(currentType) ||
    currentType === AggregatorCredentialsFormType.AuthenticationSucceeded
  ) {
    // Handled by the effect above; avoid rendering a form with a live Submit.
    return null;
  }

  if (!isSupportedFormType(currentType)) {
    // Form types that only occur when adding an account (manual MFA, manual
    // accounts, OAuth). Surfacing this explicitly beats a generic failure: the
    // user learns it is not their fault and that support can help.
    return (
      <Alert
        onClose={onCancel}
        severity='info'
      >
        <Typography variant='body2'>
          {t('connectionError.credentials.unsupported')}
        </Typography>
      </Alert>
    );
  }

  return (
    <AggregatorCredentialsForm
      institutionName={institutionName}
      isSubmitting={isSubmitting}
      key={step}
      model={current}
      onCancel={onCancel}
      onSubmit={(filled) => void handleSubmit(filled)}
    />
  );
};
